import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../state/store';
import { restoreReduxSession } from '../state/authentication/authenticationSlice';
import { LocalStorageAuth } from '../utils/LocalStorageAuth';
import { AuthMigration } from '../utils/AuthMigration';
import { Logger } from '../utils/Logger';

export const useAuthenticationInit = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { user, loading } = useSelector((state: RootState) => state.authentication);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasAttemptedRestore, setHasAttemptedRestore] = useState(false);

    useEffect(() => {
        const initializeAuth = async () => {
            // Only run once per component mount
            if (isInitialized) return;
            
            Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", "useAuthenticationInit: Starting authentication initialization");
            
            // Check if migration is needed and perform it
            if (AuthMigration.isMigrationNeeded()) {
                Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", "useAuthenticationInit: Migration needed, performing migration");
                AuthMigration.migrateIfNeeded();
            }
            
            // Check if we have authentication data in local storage
            const token = LocalStorageAuth.getToken();
            const storedUser = LocalStorageAuth.getUser();
            const storedCompany = LocalStorageAuth.getCompany();
            
            Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", `useAuthenticationInit: Token from local storage: ${token ? 'exists' : 'not found'}`);
            Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", `useAuthenticationInit: User from local storage: ${storedUser ? 'exists' : 'not found'}`);
            Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", `useAuthenticationInit: Company from local storage: ${storedCompany ? 'exists' : 'not found'}`);
            
            // If we have a token but no user, attempt to restore session
            if (token && !user && !hasAttemptedRestore) {
                Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", "useAuthenticationInit: Token found, attempting to restore session");
                setHasAttemptedRestore(true);
                
                try {
                    await dispatch(restoreReduxSession()).unwrap();
                    Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", "useAuthenticationInit: Session restored successfully");
                } catch (error) {
                    Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", `useAuthenticationInit: Failed to restore session: ${error}`);
                    // Clear invalid token from local storage
                    LocalStorageAuth.clearAll();
                }
            } else if (!token && !user) {
                Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", "useAuthenticationInit: No token found, user will need to login");
            }
            
            Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", "useAuthenticationInit: Authentication initialization complete");
            setIsInitialized(true);
        };

        // Always run initialization, don't wait for loading
        Logger.debug("st-ck-client/src/hooks/useAuthenticationInit.ts", `useAuthenticationInit: Effect triggered - loading: ${loading}, isInitialized: ${isInitialized}`);
        initializeAuth();
    }, [dispatch, user, isInitialized, hasAttemptedRestore]);



    return {
        isInitialized,
        user,
        loading
    };
};
